import {
  createContext, useContext, useRef, useState, useCallback, useEffect, ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import {
  ConnectionState,
  LocalTrack,
  LocalTrackPublication,
  Participant,
  RemoteParticipant,
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
  TrackPublication,
} from 'livekit-client';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import {
  actOnCall,
  actOnCallKeepalive,
  addCallParticipants,
  createCallEventId,
  getCallSnapshot,
  getLiveKitCredentials,
  startCall,
  syncCalls,
} from '../api/calls';
import type { CallEventEnvelope, CallLifecycleAction, CallSnapshot } from '../types/calls';
import { isDirectSfuSnapshot } from '../types/calls';
import { startRecordingForCall, useCallRecording } from '../hooks/call/useCallRecording';
import { getConversation, addMembers } from '../api/chat';
import {
  startRingtone, startRingback, stopAllCallSounds, playConnectTone, playEndTone,
} from '../utils/call/callSounds';
import { setCallActive, isOtherCallActive } from '../utils/call/callBusy';
import { releaseContinuedCall, wasOnDirectCall } from '../utils/call/continuedCalls';
import { cameraErrorMessage } from '../utils/call/cameraErrors';
import { canShareScreen, screenShareErrorMessage } from '../utils/call/screenShare';
import GroupCallScreen from '../components/call/GroupCallScreen';
import StartCallPicker from '../components/calls/StartCallPicker';
import { useDndQuietMode } from './DndQuietModeContext';
import {
  selectProtectIpAddress, selectSelectedAudioOutputId, selectAudioMutedByDefault,
} from '../redux/slices/chatPreferences';

export type GroupCallStatus = 'idle' | 'incoming' | 'connecting' | 'connected' | 'ended';

export interface GroupParticipant {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
  connState: RTCPeerConnectionState | 'new';
  speaking?: boolean;
  /** Their camera to show in the tile; null/absent shows the avatar. */
  videoTrack?: RemoteTrack | null;
  /** Their shared screen, shown across the grid. */
  screenTrack?: RemoteTrack | null;
}

export interface GroupCallState {
  status: GroupCallStatus;
  callId: string | null;
  conversationId: string | null;
  conversationName: string | null;
  starterName: string | null;
  participants: GroupParticipant[];
  muted: boolean;
  startedAt: number | null;
  /** This side's live camera (self-view), or null while it is off. */
  localVideoTrack?: LocalTrack | null;
  /** A camera toggle is still in flight. */
  cameraBusy?: boolean;
  /** This side is sharing its screen. */
  screenSharing?: boolean;
  /** A screen-share toggle is still in flight. */
  screenShareBusy?: boolean;
}

const IDLE: GroupCallState = {
  status: 'idle', callId: null, conversationId: null, conversationName: null,
  starterName: null, participants: [], muted: false, startedAt: null,
  localVideoTrack: null, cameraBusy: false, screenSharing: false, screenShareBusy: false,
};

const RING_TIMEOUT_MS = 45_000;

export interface GroupCallStartOptions {
  /** The live 1:1 this group call continues (Add people): it stays up until the server agrees. */
  continuesCallId?: string;
}

/**
 * "unsupported": the server cannot continue that 1:1 (an older server, or the
 * 1:1 is not live there); the 1:1 is untouched and nothing was shown.
 */
export type GroupCallStartResult = 'started' | 'unsupported' | 'failed';

function isVersionConflict(error: any): boolean {
  return error?.response?.data?.detail?.code === 'version_conflict';
}

function isContinuationUnsupported(error: any): boolean {
  const status = error?.response?.status;
  return (status === 409 && !isVersionConflict(error)) || status === 400 || status === 422;
}

interface GroupCallApi {
  groupCall: GroupCallState;
  startGroupCall: (conversationId: string, options?: GroupCallStartOptions) => Promise<GroupCallStartResult>;
  acceptGroupCall: () => Promise<void>;
  declineGroupCall: () => void;
  leaveGroupCall: () => void;
  toggleMute: () => void;
  /** Turn this side's camera on or off; the microphone is left as it is. */
  toggleVideo: () => void;
  addParticipants: (userIds: string[]) => Promise<void>;
  handleCallState: (event: CallEventEnvelope) => void;
  recoverCalls: () => Promise<void>;
}

const Ctx = createContext<GroupCallApi | null>(null);

function useOptionalPreference<T>(selector: (state: any) => T, fallback: T): T {
  try { return useSelector(selector); } catch { return fallback; }
}

const isTerminal = (snapshot: CallSnapshot) => (
  snapshot.status === 'ended' || snapshot.status === 'declined' || snapshot.status === 'missed'
);

export function GroupCallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isDndQuiet } = useDndQuietMode();
  const protectIpAddress = useOptionalPreference(selectProtectIpAddress, false);
  const selectedAudioOutputId = useOptionalPreference(selectSelectedAudioOutputId, null);
  const audioMutedByDefault = useOptionalPreference(selectAudioMutedByDefault, false);
  const [groupCall, setGroupCall] = useState<GroupCallState>(IDLE);
  const [addingOpen, setAddingOpen] = useState(false);
  const roomRef = useRef<Room | null>(null);
  const routeRef = useRef<{ callId: string; conversationId: string } | null>(null);
  const snapshotRef = useRef<CallSnapshot | null>(null);
  const statusRef = useRef<GroupCallStatus>('idle');
  const mutedRef = useRef(false);
  const startingRef = useRef(false);
  const acceptingRef = useRef(false);
  const closingRef = useRef(false);
  const joinedRef = useRef(false);
  const isInitiatorRef = useRef(false);
  const recoveryInFlightRef = useRef<Promise<void> | null>(null);
  const endTimerRef = useRef<{ callId: string; id: ReturnType<typeof setTimeout> } | null>(null);
  const audioHostRef = useRef<HTMLDivElement | null>(null);
  const audioSinks = useRef<Map<string, HTMLMediaElement>>(new Map());
  const remoteAudioTracks = useRef<Map<string, RemoteTrack>>(new Map());
  // Each participant's subscribed camera, kept while muted so turning it
  // back on shows it again. The tiles' own <video> elements attach it.
  const remoteVideoTracks = useRef<Map<string, RemoteTrack>>(new Map());
  const remoteScreenTracks = useRef<Map<string, RemoteTrack>>(new Map());
  const cameraBusyRef = useRef(false);
  const screenShareBusyRef = useRef(false);
  const membersRef = useRef<Record<string, { name: string | null; avatarUrl: string | null }>>({});

  const selfId = user?.id ?? '';
  statusRef.current = groupCall.status;
  mutedRef.current = groupCall.muted;

  const storeSnapshot = useCallback((next: CallSnapshot): boolean => {
    const current = snapshotRef.current;
    if (current?.callId === next.callId && current.version > next.version) return false;
    snapshotRef.current = next;
    return true;
  }, []);

  const upsertParticipant = useCallback((userId: string, patch: Partial<GroupParticipant>) => {
    if (!userId || userId === selfId) return;
    setGroupCall((current) => {
      const metadata = membersRef.current[userId] || { name: null, avatarUrl: null };
      const existing = current.participants.find((participant) => participant.userId === userId);
      const next: GroupParticipant = existing
        ? { ...existing, ...patch }
        : { userId, name: metadata.name, avatarUrl: metadata.avatarUrl, connState: 'new', ...patch };
      return {
        ...current,
        participants: [...current.participants.filter((participant) => participant.userId !== userId), next],
      };
    });
  }, [selfId]);

  /** Patch a tile that is already shown; never re-adds one that left. */
  const setParticipantVideo = useCallback((userId: string, videoTrack: RemoteTrack | null) => {
    setGroupCall((current) => (current.participants.some((participant) => participant.userId === userId)
      ? {
        ...current,
        participants: current.participants.map((participant) => (
          participant.userId === userId ? { ...participant, videoTrack } : participant
        )),
      }
      : current));
  }, []);

  /** Report this side's camera as the room has it, not as last requested. */
  /** Report this side's screen share as the room has it (the browser's own Stop ends it). */
  const syncLocalScreen = useCallback((room: Room) => {
    if (roomRef.current !== room) return;
    const sharing = room.localParticipant.isScreenShareEnabled;
    setGroupCall((current) => (current.screenSharing === sharing ? current : { ...current, screenSharing: sharing }));
  }, []);

  const syncLocalCamera = useCallback((room: Room) => {
    if (roomRef.current !== room) return;
    const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const live = publication && !publication.isMuted ? publication.track ?? null : null;
    setGroupCall((current) => (current.localVideoTrack === live ? current : { ...current, localVideoTrack: live }));
  }, []);

  const detachRemoteTrack = useCallback((userId: string, track?: RemoteTrack) => {
    const current = track ?? remoteAudioTracks.current.get(userId);
    if (current) {
      for (const element of current.detach()) element.remove();
    }
    if (!track || remoteAudioTracks.current.get(userId) === track) remoteAudioTracks.current.delete(userId);
    const sink = audioSinks.current.get(userId);
    if (sink) {
      sink.remove();
      audioSinks.current.delete(userId);
    }
  }, []);

  const attachRemoteTrack = useCallback((userId: string, track: RemoteTrack) => {
    if (track.kind === Track.Kind.Video) {
      if (track.source === Track.Source.ScreenShare) {
        remoteScreenTracks.current.set(userId, track);
        upsertParticipant(userId, { screenTrack: track });
        return;
      }
      // Cameras; a muted one is remembered but shows the avatar.
      if (track.source !== Track.Source.Camera) return;
      remoteVideoTracks.current.set(userId, track);
      upsertParticipant(userId, { videoTrack: track.isMuted ? null : track });
      return;
    }
    if (track.kind !== Track.Kind.Audio) return;
    detachRemoteTrack(userId);
    const element = track.attach();
    element.autoplay = true;
    if (selectedAudioOutputId) {
      const setSinkId = (element as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId;
      if (typeof setSinkId === 'function') {
        void setSinkId.call(element, selectedAudioOutputId).catch(() => {
          toast.info('Selected audio output is unavailable; using the default speaker.');
        });
      }
    }
    audioHostRef.current?.appendChild(element);
    audioSinks.current.set(userId, element);
    remoteAudioTracks.current.set(userId, track);
  }, [detachRemoteTrack, selectedAudioOutputId, upsertParticipant]);

  const disposeRoom = useCallback((room: Room | null) => {
    if (!room) return;
    if (roomRef.current === room) roomRef.current = null;
    room.removeAllListeners();
    // Every local capture, the camera included, so the camera light goes out
    // now rather than whenever the disconnect completes.
    room.localParticipant.trackPublications.forEach((publication) => publication.track?.stop());
    remoteAudioTracks.current.forEach((track, userId) => detachRemoteTrack(userId, track));
    remoteAudioTracks.current.clear();
    // Video elements belong to the tiles, which unmount with the call.
    remoteVideoTracks.current.clear();
    remoteScreenTracks.current.clear();
    audioSinks.current.forEach((element) => element.remove());
    audioSinks.current.clear();
    void room.disconnect(true).catch(() => { /* local cleanup is best-effort */ });
  }, [detachRemoteTrack]);

  const cancelEndTimer = useCallback(() => {
    if (!endTimerRef.current) return;
    clearTimeout(endTimerRef.current.id);
    endTimerRef.current = null;
  }, []);

  const cleanup = useCallback((ended: boolean) => {
    const endedCallId = routeRef.current?.callId ?? null;
    // A duplicate completion can arrive after the terminal call-state has
    // already cleared the route. Retain that call's timer instead of replacing
    // it with an unkeyed callback that could affect a later call.
    if (ended && !endedCallId && endTimerRef.current) return;
    closingRef.current = true;
    stopAllCallSounds();
    disposeRoom(roomRef.current);
    routeRef.current = null;
    snapshotRef.current = null;
    startingRef.current = false;
    acceptingRef.current = false;
    joinedRef.current = false;
    isInitiatorRef.current = false;
    mutedRef.current = false;
    cameraBusyRef.current = false;
    screenShareBusyRef.current = false;
    setAddingOpen(false);
    setCallActive('group', false);
    cancelEndTimer();
    // A late completion (e.g. a dropped room reporting after the terminal
    // frame already cleared the call and its "Call ended" card) has no call to
    // show as ended: clear quietly instead of re-showing a card no timer
    // would ever remove.
    if (ended && endedCallId) {
      setGroupCall({ ...IDLE, status: 'ended' });
      const id = setTimeout(() => {
        if (endTimerRef.current?.callId !== endedCallId) return;
        endTimerRef.current = null;
        setGroupCall((current) => current.status === 'ended' ? IDLE : current);
      }, 1500);
      endTimerRef.current = { callId: endedCallId, id };
    } else {
      setGroupCall(IDLE);
    }
    closingRef.current = false;
  }, [cancelEndTimer, disposeRoom]);

  const loadMembers = useCallback(async (conversationId: string) => {
    const conversation: any = await getConversation(conversationId);
    const members: Record<string, { name: string | null; avatarUrl: string | null }> = {};
    for (const member of conversation?.members || []) {
      const userId = member.user_id ?? member.id;
      if (userId) {
        members[userId] = {
          name: member.name ?? member.display_name ?? null,
          avatarUrl: member.avatar_url ?? null,
        };
      }
    }
    membersRef.current = members;
    const conversationName = conversation?.name ?? (conversation?.type === 'dm' ? null : 'Group call');
    setGroupCall((current) => ({
      ...current,
      conversationName,
      starterName: current.starterName && snapshotRef.current
        ? members[snapshotRef.current.initiatorUserId]?.name ?? current.starterName
        : current.starterName,
    }));
    return Object.keys(members).filter((userId) => userId !== selfId);
  }, [selfId]);

  const applyAuthoritativeAction = useCallback(async (
    action: CallLifecycleAction,
    initial?: CallSnapshot,
  ): Promise<CallSnapshot | null> => {
    const route = routeRef.current;
    if (!route) return null;
    const eventId = createCallEventId(`group-${action}`);
    let current = initial?.callId === route.callId
      ? initial
      : snapshotRef.current?.callId === route.callId ? snapshotRef.current : null;
    try {
      current ??= await getCallSnapshot(route.callId);
      if (current.callId !== route.callId) return null;
      storeSnapshot(current);
      if (isTerminal(current)) return current;
      const next = await actOnCall(route.callId, {
        action, eventId, expectedVersion: current.version,
      });
      if (next.callId !== route.callId) return null;
      storeSnapshot(next);
      return next;
    } catch (error: any) {
      if (error?.response?.status !== 409) return null;
      try {
        const latest = await getCallSnapshot(route.callId);
        if (latest.callId !== route.callId) return null;
        storeSnapshot(latest);
        if (isTerminal(latest)) return latest;
        if (action === 'answer' && latest.status === 'active') return latest;
        const retried = await actOnCall(route.callId, {
          action, eventId, expectedVersion: latest.version,
        });
        if (retried.callId !== route.callId) return null;
        storeSnapshot(retried);
        return retried;
      } catch {
        return null;
      }
    }
  }, [storeSnapshot]);

  const leaveAfterConnectionFailure = useCallback(async () => {
    const current = snapshotRef.current;
    if (!current || current.status !== 'active') return;
    const action: CallLifecycleAction = isInitiatorRef.current ? 'end' : 'participant-left';
    await applyAuthoritativeAction(action, current);
  }, [applyAuthoritativeAction]);

  const connectRoom = useCallback(async (active: CallSnapshot): Promise<CallSnapshot> => {
    if (active.status !== 'active' || active.mediaTopology !== 'sfu' || !active.isGroup) {
      throw new Error('Group call is not ready');
    }
    const route = routeRef.current;
    if (!route || route.callId !== active.callId) throw new Error('Group call changed');

    const onCameraMuteChanged = (target: Room, publication: TrackPublication, participant: Participant) => {
      if (publication.source !== Track.Source.Camera) return;
      if (participant === target.localParticipant) {
        // The camera stopped on its own (unplugged, or taken by the OS).
        syncLocalCamera(target);
        return;
      }
      const track = remoteVideoTracks.current.get(participant.identity);
      if (!track || publication.track !== track) return;
      setParticipantVideo(participant.identity, publication.isMuted ? null : track);
    };
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      rtcConfig: { iceTransportPolicy: protectIpAddress ? 'relay' : 'all' },
    });
    roomRef.current = room;
    room
      .on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        upsertParticipant(participant.identity, { connState: 'connected', speaking: participant.isSpeaking });
      })
      .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        detachRemoteTrack(participant.identity);
        remoteVideoTracks.current.delete(participant.identity);
        remoteScreenTracks.current.delete(participant.identity);
        setGroupCall((current) => ({
          ...current,
          participants: current.participants.filter((item) => item.userId !== participant.identity),
        }));
      })
      .on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
        attachRemoteTrack(participant.identity, track);
        upsertParticipant(participant.identity, { connState: 'connected' });
      })
      .on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
        if (remoteScreenTracks.current.get(participant.identity) === track) {
          remoteScreenTracks.current.delete(participant.identity);
          setGroupCall((current) => ({
            ...current,
            participants: current.participants.map((item) => (
              item.userId === participant.identity ? { ...item, screenTrack: null } : item
            )),
          }));
          return;
        }
        if (remoteVideoTracks.current.get(participant.identity) === track) {
          remoteVideoTracks.current.delete(participant.identity);
          setParticipantVideo(participant.identity, null);
          return;
        }
        // Never the audio sink for a video track (that silenced the peer).
        if (track.kind === Track.Kind.Audio) detachRemoteTrack(participant.identity, track);
      })
      // A camera turned off is muted, not unpublished: the tile goes back to
      // the avatar until it is turned on again.
      .on(RoomEvent.TrackMuted, (publication: TrackPublication, participant: Participant) => {
        onCameraMuteChanged(room, publication, participant);
      })
      .on(RoomEvent.TrackUnmuted, (publication: TrackPublication, participant: Participant) => {
        onCameraMuteChanged(room, publication, participant);
      })
      .on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication) => {
        if (publication.source === Track.Source.Camera) syncLocalCamera(room);
        if (publication.source === Track.Source.ScreenShare) syncLocalScreen(room);
      })
      .on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication) => {
        if (publication.source === Track.Source.ScreenShare) syncLocalScreen(room);
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        const activeIds = new Set(speakers.map((participant) => participant.identity));
        setGroupCall((current) => ({
          ...current,
          participants: current.participants.map((participant) => ({
            ...participant, speaking: activeIds.has(participant.userId),
          })),
        }));
      })
      .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        if (roomRef.current !== room) return;
        if (state === ConnectionState.Reconnecting) {
          void applyAuthoritativeAction('media-reconnecting');
          setGroupCall((current) => ({
            ...current,
            participants: current.participants.map((participant) => ({ ...participant, connState: 'disconnected' })),
          }));
        } else if (state === ConnectionState.Connected) {
          void applyAuthoritativeAction('media-connected');
          setGroupCall((current) => ({
            ...current, status: 'connected',
            participants: current.participants.map((participant) => ({ ...participant, connState: 'connected' })),
          }));
        }
      })
      .on(RoomEvent.Disconnected, () => {
        if (roomRef.current !== room || closingRef.current) return;
        void applyAuthoritativeAction('media-disconnected');
        toast.error('Group call connection was lost. Check your connection and try again.');
        void leaveAfterConnectionFailure().finally(() => cleanup(true));
      });

    try {
      const credentials = await getLiveKitCredentials(active.callId);
      if (
        credentials.callId !== active.callId
        || !credentials.serverUrl
        || !credentials.participantToken
      ) throw new Error('Invalid LiveKit credentials');
      await room.connect(credentials.serverUrl, credentials.participantToken);
      if (roomRef.current !== room) throw new Error('Group call changed');
      await room.localParticipant.setMicrophoneEnabled(audioMutedByDefault ? false : !mutedRef.current);
      room.remoteParticipants.forEach((participant) => {
        upsertParticipant(participant.identity, { connState: 'connected', speaking: participant.isSpeaking });
        participant.trackPublications.forEach((publication) => {
          if (publication.track) attachRemoteTrack(participant.identity, publication.track);
        });
      });
      const joined = await applyAuthoritativeAction('participant-joined', active);
      if (!joined || joined.status !== 'active') throw new Error('Could not record group participant');
      joinedRef.current = true;
      setGroupCall((current) => ({
        ...current, status: 'connected', startedAt: current.startedAt ?? Date.now(),
      }));
      return joined;
    } catch (error) {
      disposeRoom(room);
      throw error;
    }
  }, [
    applyAuthoritativeAction, attachRemoteTrack, cleanup, detachRemoteTrack,
    disposeRoom, leaveAfterConnectionFailure, upsertParticipant, setParticipantVideo, syncLocalCamera, syncLocalScreen,
    protectIpAddress, audioMutedByDefault,
  ]);

  const startGroupCall = useCallback(async (
    conversationId: string,
    { continuesCallId }: GroupCallStartOptions = {},
  ): Promise<GroupCallStartResult> => {
    // Continuing a 1:1 runs while that 1:1 is still up; it ends once the server agrees.
    if (routeRef.current || startingRef.current || (!continuesCallId && isOtherCallActive('group'))) return 'failed';
    const startEventId = createCallEventId('group-start');
    // Reserve the group route before any await. An incoming SFU frame observed
    // while members are loading is deliberately ignored rather than adopted
    // and then overwritten by this outgoing session.
    const reservation = { callId: `pending:${startEventId}`, conversationId };
    cancelEndTimer();
    routeRef.current = reservation;
    startingRef.current = true;
    setCallActive('group', true);
    let ownsStart = true;
    try {
      const participantUserIds = await loadMembers(conversationId);
      let created: CallSnapshot;
      try {
        created = await startCall({
          clientEventId: startEventId,
          conversationId,
          participantUserIds,
          callType: 'voice',
          ...(continuesCallId ? { continuesCallId } : {}),
        });
      } catch (error) {
        if (!continuesCallId) throw error;
        // The 1:1 is still on: leave it be and let the caller decide.
        if (routeRef.current === reservation) cleanup(false);
        return isContinuationUnsupported(error) ? 'unsupported' : 'failed';
      }
      // The server ended the 1:1; end it here without "Call ended".
      if (continuesCallId) releaseContinuedCall(continuesCallId);
      if (
        created.conversationId !== conversationId
        || created.status !== 'ringing'
        || created.mediaTopology !== 'sfu'
        || !created.isGroup
        || !created.participantUserIds.includes(selfId)
      ) throw new Error('Server did not authorize an SFU group call');
      if (routeRef.current !== reservation) {
        ownsStart = false;
        return 'failed';
      }

      routeRef.current = { callId: created.callId, conversationId };
      snapshotRef.current = created;
      isInitiatorRef.current = true;
      setCallActive('group', true);
      setGroupCall((current) => ({
        ...IDLE,
        muted: audioMutedByDefault,
        status: 'connecting',
        callId: created.callId,
        conversationId,
        conversationName: current.conversationName,
        startedAt: Date.now(),
      }));
      const active = await applyAuthoritativeAction('answer', created);
      if (!active || active.status !== 'active') throw new Error('Could not activate group call');
      await connectRoom(active);
      return 'started';
    } catch {
      if (!ownsStart) return 'failed';
      await leaveAfterConnectionFailure();
      cleanup(false);
      toast.error('Could not start the group call. Check your connection and try again.');
      return 'failed';
    } finally {
      startingRef.current = false;
    }
  }, [
    applyAuthoritativeAction, cancelEndTimer, cleanup, connectRoom, leaveAfterConnectionFailure,
    loadMembers, selfId, audioMutedByDefault,
  ]);

  const acceptGroupCall = useCallback(async () => {
    const route = routeRef.current;
    if (!route || statusRef.current !== 'incoming' || acceptingRef.current || roomRef.current) return;
    acceptingRef.current = true;
    stopAllCallSounds();
    setGroupCall((current) => ({ ...current, muted: audioMutedByDefault || current.muted, status: 'connecting' }));
    try {
      let current = snapshotRef.current?.callId === route.callId
        ? snapshotRef.current
        : await getCallSnapshot(route.callId);
      if (current.callId !== route.callId || current.mediaTopology !== 'sfu' || !current.isGroup) {
        throw new Error('Unauthorized group call');
      }
      storeSnapshot(current);
      // Accept is per participant: a room someone else already opened still
      // needs this invitee's accept before LiveKit will issue it a token.
      if (current.status === 'ringing' || current.status === 'active') {
        const active = await applyAuthoritativeAction('answer', current);
        if (!active) throw new Error('Could not activate group call');
        current = active;
      }
      if (current.status !== 'active') throw new Error('Group call has ended');
      await connectRoom(current);
    } catch {
      await leaveAfterConnectionFailure();
      cleanup(false);
      toast.error('Could not join the group call. Check your connection and try again.');
    } finally {
      acceptingRef.current = false;
    }
  }, [
    applyAuthoritativeAction, cleanup, connectRoom, leaveAfterConnectionFailure, storeSnapshot,
    audioMutedByDefault,
  ]);

  const declineGroupCall = useCallback(() => {
    if (statusRef.current !== 'incoming') return;
    const current = snapshotRef.current;
    if (current?.status === 'ringing') {
      void applyAuthoritativeAction('decline', current).finally(() => cleanup(false));
    } else {
      cleanup(false);
    }
  }, [applyAuthoritativeAction, cleanup]);

  const leaveGroupCall = useCallback(() => {
    if (statusRef.current === 'idle' || statusRef.current === 'ended' || closingRef.current) return;
    closingRef.current = true;
    const current = snapshotRef.current;
    const shouldAct = current?.status === 'active' && (joinedRef.current || isInitiatorRef.current);
    const action: CallLifecycleAction = isInitiatorRef.current ? 'end' : 'participant-left';
    if (shouldAct) {
      void applyAuthoritativeAction(action, current).finally(() => cleanup(true));
    } else {
      cleanup(true);
    }
  }, [applyAuthoritativeAction, cleanup]);

  const toggleMute = useCallback(() => {
    setGroupCall((current) => {
      const muted = !current.muted;
      mutedRef.current = muted;
      void roomRef.current?.localParticipant.setMicrophoneEnabled(!muted).catch(() => {
        toast.error('Could not update the microphone. Check device permissions and try again.');
        setGroupCall((latest) => ({ ...latest, muted: !muted }));
        mutedRef.current = !muted;
      });
      return { ...current, muted };
    });
  }, []);

  const toggleVideo = useCallback(() => {
    const room = roomRef.current;
    if (!room || cameraBusyRef.current) return;
    const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const enable = !(publication && !publication.isMuted && publication.track);
    cameraBusyRef.current = true;
    setGroupCall((current) => ({ ...current, cameraBusy: true }));
    void room.localParticipant.setCameraEnabled(enable).then((published) => {
      // Left the call while the camera was starting: the room's teardown
      // already ran, so release this capture here or the camera stays lit.
      if (roomRef.current !== room) published?.track?.stop();
    }, (error) => {
      if (roomRef.current === room) toast.error(cameraErrorMessage(error));
    }).finally(() => {
      if (roomRef.current !== room) return;
      cameraBusyRef.current = false;
      syncLocalCamera(room);
      setGroupCall((current) => ({ ...current, cameraBusy: false }));
    });
  }, [syncLocalCamera]);

  const toggleScreenShare = useCallback(() => {
    const room = roomRef.current;
    if (!room || screenShareBusyRef.current) return;
    const enable = !room.localParticipant.isScreenShareEnabled;
    screenShareBusyRef.current = true;
    setGroupCall((current) => ({ ...current, screenShareBusy: true }));
    void room.localParticipant.setScreenShareEnabled(enable, { audio: false }).then((published) => {
      // Left the call while the browser's picker was open.
      if (roomRef.current !== room) published?.track?.stop();
    }, (error) => {
      const message = enable ? screenShareErrorMessage(error) : null;
      if (message && roomRef.current === room) toast.error(message);
    }).finally(() => {
      if (roomRef.current !== room) return;
      screenShareBusyRef.current = false;
      syncLocalScreen(room);
      setGroupCall((current) => ({ ...current, screenShareBusy: false }));
    });
  }, [syncLocalScreen]);

  const addParticipants = useCallback(async (userIds: string[]) => {
    const route = routeRef.current;
    let current = snapshotRef.current;
    if (!route || !current || !userIds.length) return;
    const eventId = createCallEventId('group-participants-added');
    try {
      await addMembers(route.conversationId, userIds);
      await loadMembers(route.conversationId);
      try {
        const next = await addCallParticipants(route.callId, {
          participantUserIds: userIds, eventId, expectedVersion: current.version,
        });
        storeSnapshot(next);
      } catch (error: any) {
        if (error?.response?.status !== 409) throw error;
        current = await getCallSnapshot(route.callId);
        storeSnapshot(current);
        const next = await addCallParticipants(route.callId, {
          participantUserIds: userIds, eventId, expectedVersion: current.version,
        });
        storeSnapshot(next);
      }
    } catch {
      toast.error('Could not add participants. Check your connection and try again.');
    }
  }, [loadMembers, storeSnapshot]);

  /** `carriedOver`: this user was on the 1:1 this group continues, so it joins without ringing. */
  const reconcileSnapshot = useCallback((next: CallSnapshot, carriedOver = false) => {
    const route = routeRef.current;
    if (route && route.callId !== next.callId) return;
    if (!route) {
      if (
        isTerminal(next)
        || next.initiatorUserId === selfId
        || !next.participantUserIds.includes(selfId)
        || (!carriedOver && isDndQuiet)
        || isOtherCallActive('group')
      ) return;
      if (!storeSnapshot(next)) return;
      cancelEndTimer();
      routeRef.current = { callId: next.callId, conversationId: next.conversationId };
      isInitiatorRef.current = false;
      setCallActive('group', true);
      void loadMembers(next.conversationId).catch(() => { /* display metadata is best-effort */ });
      setGroupCall({
        ...IDLE,
        status: 'incoming',
        callId: next.callId,
        conversationId: next.conversationId,
        starterName: membersRef.current[next.initiatorUserId]?.name ?? next.initiatorUserId,
      });
      if (carriedOver) {
        // Already talking to them: join at once (the same render goes
        // straight to connecting, so it never rings).
        statusRef.current = 'incoming';
        void acceptGroupCall();
      }
      return;
    }
    if (!storeSnapshot(next)) return;
    if (isTerminal(next)) {
      cleanup(true);
      return;
    }
    if (next.conversationId === route.conversationId) {
      void loadMembers(next.conversationId).catch(() => { /* display metadata is best-effort */ });
    }
  }, [acceptGroupCall, cancelEndTimer, cleanup, isDndQuiet, loadMembers, selfId, storeSnapshot]);

  const handleCallState = useCallback((event: CallEventEnvelope) => {
    const next = event?.snapshot;
    if (
      !next
      || event.callId !== next.callId
      || event.eventId !== next.eventId
      || event.sequenceNumber !== next.version
      || next.mediaTopology !== 'sfu'
      || !next.isGroup
      // A direct SFU call is a 1:1 (CallContext owns it), not a group ring.
      || isDirectSfuSnapshot(next)
    ) return;
    // The person on the 1:1 this group continues (Add people) joins it without
    // ringing, in the tab that was on that 1:1; their other tabs ring as usual.
    const carriedOver = !routeRef.current
      && Boolean(event.carriedOverUserIds?.includes(selfId))
      && wasOnDirectCall(event.continuesCallId);
    if (carriedOver && event.continuesCallId) releaseContinuedCall(event.continuesCallId);
    const previous = snapshotRef.current;
    if (previous?.callId === next.callId && next.version > previous.version + 1) {
      void getCallSnapshot(next.callId)
        .then((latest) => reconcileSnapshot(latest, carriedOver))
        .catch(() => { /* next state frame retries */ });
      return;
    }
    reconcileSnapshot(next, carriedOver);
  }, [reconcileSnapshot, selfId]);

  const recoverCalls = useCallback((): Promise<void> => {
    if (recoveryInFlightRef.current) return recoveryInFlightRef.current;

    const recovery = (async () => {
      try {
        const { calls } = await syncCalls();
        const route = routeRef.current;
        if (route) {
          // A pending outgoing reservation has no server call ID yet. Its
          // start path owns adoption once the create response arrives.
          if (route.callId.startsWith('pending:')) return;
          const routed = calls.find((item) => item.callId === route.callId);
          if (routed) reconcileSnapshot(routed);
          return;
        }
        if (statusRef.current !== 'idle' && statusRef.current !== 'ended') return;

        const incoming = calls.find((item) => (
          item.mediaTopology === 'sfu'
          && item.isGroup
          && !isDirectSfuSnapshot(item)
          && !isTerminal(item)
          && item.initiatorUserId !== selfId
          && item.participantUserIds.includes(selfId)
        ));
        if (incoming) reconcileSnapshot(incoming);
      } catch {
        // A following websocket reconnect or call-state frame retries sync.
      } finally {
        recoveryInFlightRef.current = null;
      }
    })();
    recoveryInFlightRef.current = recovery;
    return recovery;
  }, [reconcileSnapshot, selfId]);

  useEffect(() => {
    switch (groupCall.status) {
      case 'incoming': startRingtone(); break;
      case 'connecting': startRingback(); break;
      case 'connected': stopAllCallSounds(); playConnectTone(); break;
      case 'ended': stopAllCallSounds(); playEndTone(); break;
      default: stopAllCallSounds(); break;
    }
  }, [groupCall.status]);

  useEffect(() => {
    if (groupCall.status !== 'incoming') return;
    const id = setTimeout(() => cleanup(false), RING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [groupCall.status, cleanup]);

  useEffect(() => {
    const onUnload = () => {
      const route = routeRef.current;
      const current = snapshotRef.current;
      if (route && current?.callId === route.callId && current.status === 'active' && joinedRef.current) {
        actOnCallKeepalive(route.callId, {
          action: isInitiatorRef.current ? 'end' : 'participant-left',
          eventId: createCallEventId('group-unload'),
          expectedVersion: current.version,
        });
      }
      stopAllCallSounds();
      disposeRoom(roomRef.current);
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, [disposeRoom]);

  useEffect(() => () => {
    cancelEndTimer();
    stopAllCallSounds();
    disposeRoom(roomRef.current);
    setCallActive('group', false);
  }, [cancelEndTimer, disposeRoom]);

  // Recording state rides its own channel; failures never touch the call.
  const recording = useCallRecording(
    groupCall.status === 'idle' || groupCall.status === 'ended' ? null : groupCall.callId,
    groupCall.status === 'connected',
  );
  const recordingCallId = recording?.call_id ?? null;
  const startRecording = useCallback(
    () => (recordingCallId ? startRecordingForCall(recordingCallId) : Promise.resolve()),
    [recordingCallId],
  );

  const apiValue: GroupCallApi = {
    groupCall,
    startGroupCall,
    acceptGroupCall,
    declineGroupCall,
    leaveGroupCall,
    toggleMute,
    toggleVideo,
    addParticipants,
    handleCallState,
    recoverCalls,
  };

  return (
    <Ctx.Provider value={apiValue}>
      {children}
      {groupCall.status !== 'idle' && (
        <GroupCallScreen
          state={groupCall}
          recordingStatus={recording?.status ?? null}
          recordingStartMode={recording?.start_mode ?? null}
          canStartRecording={!!recording?.can_start}
          onStartRecording={startRecording}
          onAccept={acceptGroupCall}
          onDecline={declineGroupCall}
          onLeave={leaveGroupCall}
          onToggleMute={toggleMute}
          onToggleVideo={groupCall.status === 'connected' ? toggleVideo : undefined}
          onToggleScreenShare={groupCall.status === 'connected' && canShareScreen() ? toggleScreenShare : undefined}
          onAddParticipant={groupCall.status === 'connected' ? () => setAddingOpen(true) : undefined}
        />
      )}
      {addingOpen && groupCall.status === 'connected' && (
        <StartCallPicker
          multi
          title="Add to call"
          excludeUserIds={[selfId, ...groupCall.participants.map((participant) => participant.userId)]}
          onClose={() => setAddingOpen(false)}
          onPickMany={(users) => {
            setAddingOpen(false);
            void addParticipants(users.map((participant) => participant.user_id));
          }}
        />
      )}
      <div ref={audioHostRef} style={{ display: 'none' }} />
    </Ctx.Provider>
  );
}

export function useGroupCall(): GroupCallApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useGroupCall must be used within a GroupCallProvider');
  return ctx;
}
